
import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { I18nProvider } from '../i18n/i18n';
import { extractLanguageFromPath } from '@config/i18n.config';

// Define supported languages
export type Language = 'en' | 'es' | 'fr' | 'de' | 'hi';

// Define the shape of our language context
export interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  languageNames: Record<Language, string>;
}

// Language names for UI display
const languageNames: Record<Language, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  hi: 'हिन्दी',
};

// Create the context with a default value
const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  languageNames
});

// Custom hook to use the language context
export const useLanguage = () => useContext(LanguageContext);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const location = useLocation();
  
  // Detect browser language or use stored preference
  const detectLanguage = (): Language => {
    try {
      if (typeof window !== 'undefined') {
        const stored = localStorage.getItem('finanzly-language');
        if (stored && Object.keys(languageNames).includes(stored)) {
          return stored as Language;
        }
        
        const browserLang = navigator.language.split('-')[0];
        return (Object.keys(languageNames).includes(browserLang) 
          ? browserLang 
          : 'en') as Language;
      }
    } catch (error) {
      console.error('Error detecting language:', error);
    }
    return 'en';
  };

  const [language, setLanguageState] = useState<Language>('en');

  // Effect to initialize language after component mount
  useEffect(() => {
    const detectedLang = detectLanguage();
    setLanguageState(detectedLang);
  }, []);

  // Effect to update language when URL changes
  useEffect(() => {
    const langFromPath = extractLanguageFromPath(location.pathname);
    if (langFromPath && langFromPath !== language) {
      setLanguageState(langFromPath as Language);
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem('finanzly-language', langFromPath);
        }
      } catch (error) {
        console.error('Error saving language preference:', error);
      }
    }
  }, [location.pathname]); // Re-run when URL changes

  // Handler for language changes
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('finanzly-language', lang);
      }
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
  };

  // Create the context value
  const contextValue = {
    language,
    setLanguage,
    languageNames
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      <I18nProvider locale={language}>
        {children}
      </I18nProvider>
    </LanguageContext.Provider>
  );
};
